import { useState } from "react"
import type { CreateMcpServerInput, ToolCatalog } from "../api/admin-client.ts"
import { ToolMatrix } from "./ToolMatrix.tsx"

/** 서버 툴 프리셋 — 프리셋이 catalog 에서 로드되지 않았을 때의 폴백은 빈 배열. */
type PresetKey = string | "custom"

/**
 * MCP 서버 생성 마법사 — name · displayName · description? · 툴 프리셋 → ToolMatrix 로 선택.
 */
export function ServerCreateModal(props: {
	readonly slug: string
	readonly catalog: ToolCatalog | undefined
	readonly onClose: () => void
	readonly onCreate: (input: CreateMcpServerInput) => Promise<void>
}) {
	const [form, setForm] = useState({ name: "", displayName: "", description: "" })
	const [preset, setPreset] = useState<PresetKey>("reader")
	const [enabledTools, setEnabledTools] = useState<ReadonlyArray<string>>(() =>
		resolvePresetTools(props.catalog, "reader")
	)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | undefined>()

	const nameValid = /^[a-z][a-z0-9-]{0,31}$/.test(form.name)
	const canSubmit = nameValid && form.displayName.trim().length > 0 && enabledTools.length > 0 && !busy

	function selectPreset(p: PresetKey) {
		setPreset(p)
		if (p === "custom") return
		setEnabledTools(resolvePresetTools(props.catalog, p))
	}

	async function submit(e: React.FormEvent) {
		e.preventDefault()
		if (!canSubmit) return
		setBusy(true)
		setError(undefined)
		try {
			await props.onCreate({
				name: form.name,
				displayName: form.displayName.trim(),
				...(form.description.trim() ? { description: form.description.trim() } : {}),
				enabledTools
			})
			props.onClose()
		} catch (err) {
			setError((err as Error).message)
		} finally {
			setBusy(false)
		}
	}

	const presetKeys = props.catalog ? Object.keys(props.catalog.serverToolPresets) : []

	return (
		<dialog
			className="modal-backdrop"
			open
			onMouseDown={(e) => e.currentTarget === e.target && props.onClose()}
		>
			<form
				className="modal"
				onMouseDown={(e) => e.stopPropagation()}
				onSubmit={submit}
				style={{ maxWidth: 620 }}
			>
				<div className="modal-head">
					<h2>새 MCP 서버</h2>
					<p>
						<code className="inline">
							/mcp/{props.slug}/{form.name || "<name>"}
						</code>{" "}
						엔드포인트가 생성됩니다.
					</p>
				</div>

				<div className="modal-body">
					{error && (
						<div className="alert alert-danger" style={{ marginBottom: 14 }}>
							{error}
						</div>
					)}

					<div className="field">
						<label className="field-label" htmlFor="server-name">
							서버 이름
						</label>
						<input
							id="server-name"
							className="input input-mono"
							placeholder="default"
							value={form.name}
							onChange={(e) =>
								setForm({ ...form, name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })
							}
							spellCheck={false}
						/>
						<span className="field-hint">
							kebab-case · 소문자+숫자+하이픈 · 첫 글자는 문자. URL 세그먼트에 그대로 노출됩니다.
						</span>
					</div>

					<div className="field">
						<label className="field-label" htmlFor="server-display">
							표시 이름
						</label>
						<input
							id="server-display"
							className="input"
							placeholder="서버 표시 이름 (선택)"
							value={form.displayName}
							onChange={(e) => setForm({ ...form, displayName: e.target.value })}
						/>
					</div>

					<div className="field">
						<label className="field-label" htmlFor="server-desc">
							설명 (선택)
						</label>
						<input
							id="server-desc"
							className="input"
							placeholder="기본 에이전트용 엔드포인트"
							value={form.description}
							onChange={(e) => setForm({ ...form, description: e.target.value })}
						/>
					</div>

					<div className="field">
						<label className="field-label" htmlFor="server-preset">
							툴 프리셋
						</label>
						<select
							id="server-preset"
							className="input select"
							value={preset}
							onChange={(e) => selectPreset(e.target.value as PresetKey)}
						>
							{presetKeys.map((key) => (
								<option key={key} value={key}>
									{key}
								</option>
							))}
							<option value="custom">custom</option>
						</select>
						<span className="field-hint">프리셋을 선택하면 아래 체크박스가 자동 갱신됩니다.</span>
					</div>

					<div className="field">
						<div className="field-label">활성 도구 ({enabledTools.length})</div>
						<ToolMatrix
							catalog={props.catalog}
							value={enabledTools}
							onChange={(next) => {
								setEnabledTools(next)
								setPreset("custom")
							}}
						/>
					</div>
				</div>

				<div className="modal-foot">
					<button type="button" className="btn btn-ghost" onClick={props.onClose} disabled={busy}>
						취소
					</button>
					<button type="submit" className="btn btn-primary" disabled={!canSubmit}>
						{busy ? "생성 중..." : "생성"}
					</button>
				</div>
			</form>
		</dialog>
	)
}

function resolvePresetTools(catalog: ToolCatalog | undefined, preset: string): ReadonlyArray<string> {
	if (!catalog) return []
	return catalog.serverToolPresets[preset] ?? []
}
